import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";

interface OverrideRow {
  product_id: number;
  jan: string | null;
  product_name: string | null;
  sync_enabled: boolean;
}

interface ConnectionRow {
  connection_id: number;
  shop_id: string;
  shop_name: string | null;
  country_code: string;
  default_enabled: boolean;
  overrides: OverrideRow[];
}

interface Product {
  id: number;
  jan: string;
  product_name: string;
}

interface EditState {
  default_enabled: boolean;
  // product_id -> sync_enabled (true/false)。entry が無ければデフォルトに従う。
  overrides: Record<number, boolean>;
  // 編集中に削除した override (= デフォルトに戻す)
  removed: Set<number>;
  dirty: boolean;
}

/**
 * 顧客側: Shopee 在庫同期設定画面 (本実装)。
 *
 * 設計 §4 のイメージ図に沿って:
 *  - 店舗ごとに default ON/OFF
 *  - 商品別の override (default と異なる商品のみ)
 *  - 1 接続まとめて保存
 */
export function ShopeeSyncSettingsPage() {
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [edits, setEdits] = useState<Record<number, EditState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingConnId, setSavingConnId] = useState<number | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // 個別追加用の検索
  const [productSearch, setProductSearch] = useState<Record<number, string>>({});
  const [productResults, setProductResults] = useState<Record<number, Product[]>>(
    {},
  );

  const initEditFromConnection = (c: ConnectionRow): EditState => ({
    default_enabled: c.default_enabled,
    overrides: Object.fromEntries(
      c.overrides.map((o) => [o.product_id, o.sync_enabled]),
    ),
    removed: new Set(),
    dirty: false,
  });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return api
      .get<{ connections: ConnectionRow[] }>("/account/shopee/sync-settings")
      .then((res) => {
        setConnections(res.data.connections);
        const next: Record<number, EditState> = {};
        res.data.connections.forEach((c) => {
          next[c.connection_id] = initEditFromConnection(c);
        });
        setEdits(next);
      })
      .catch((e) => setError(apiErrorMessage(e, "在庫同期設定の取得に失敗しました")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setEditField = (connectionId: number, patch: Partial<EditState>) => {
    setEdits((cur) => {
      const before = cur[connectionId];
      if (!before) return cur;
      return {
        ...cur,
        [connectionId]: { ...before, ...patch, dirty: true },
      };
    });
  };

  const toggleDefault = (connectionId: number, enabled: boolean) => {
    setEditField(connectionId, { default_enabled: enabled });
  };

  const setOverride = (
    connectionId: number,
    product_id: number,
    enabled: boolean,
  ) => {
    setEdits((cur) => {
      const before = cur[connectionId];
      if (!before) return cur;
      const nextRemoved = new Set(before.removed);
      nextRemoved.delete(product_id);
      return {
        ...cur,
        [connectionId]: {
          ...before,
          overrides: { ...before.overrides, [product_id]: enabled },
          removed: nextRemoved,
          dirty: true,
        },
      };
    });
  };

  const removeOverride = (connectionId: number, product_id: number) => {
    setEdits((cur) => {
      const before = cur[connectionId];
      if (!before) return cur;
      const nextOverrides = { ...before.overrides };
      delete nextOverrides[product_id];
      const nextRemoved = new Set(before.removed);
      nextRemoved.add(product_id);
      return {
        ...cur,
        [connectionId]: {
          ...before,
          overrides: nextOverrides,
          removed: nextRemoved,
          dirty: true,
        },
      };
    });
  };

  const searchProducts = async (connectionId: number) => {
    const q = (productSearch[connectionId] || "").trim();
    if (q.length < 2) {
      setProductResults((s) => ({ ...s, [connectionId]: [] }));
      return;
    }
    try {
      const res = await api.get<{
        products: Product[];
      }>(`/catalog?search=${encodeURIComponent(q)}&per_page=20`);
      setProductResults((s) => ({ ...s, [connectionId]: res.data.products }));
    } catch (e) {
      setError(apiErrorMessage(e, "商品検索に失敗しました"));
    }
  };

  const handleSave = async (connectionId: number) => {
    const edit = edits[connectionId];
    if (!edit) return;
    setSavingConnId(connectionId);
    setError(null);
    setSavedFlash(null);
    try {
      // overrides の payload を組み立てる
      const overridesPayload: { product_id: number; sync_enabled: boolean | null }[] = [];
      Object.entries(edit.overrides).forEach(([pid, enabled]) => {
        overridesPayload.push({
          product_id: Number(pid),
          sync_enabled: enabled,
        });
      });
      // 削除されたものは null で送って消す
      edit.removed.forEach((pid) => {
        overridesPayload.push({ product_id: pid, sync_enabled: null });
      });
      await api.put("/account/shopee/sync-settings", {
        connection_id: connectionId,
        default_enabled: edit.default_enabled,
        overrides: overridesPayload,
      });
      setSavedFlash(`接続 ID ${connectionId} の設定を保存しました`);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "保存に失敗しました"));
    } finally {
      setSavingConnId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">Shopee 在庫同期設定</h1>
        <p className="text-sm text-gray-500">読み込み中…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Shopee 在庫同期設定</h1>
      <p className="text-sm text-gray-600">
        かんたん在庫の公開在庫数を Shopee 側に同期します。Wonect が在庫切れになった瞬間に Shopee
        側でも在庫が 0 になるので、売越を防げます。デフォルトは「同期しない (OFF)」で、Wonect
        一本化したい商品だけ「同期 ON」に切り替えるのが推奨です。
      </p>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {savedFlash && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {savedFlash}
        </div>
      )}

      {connections.length === 0 ? (
        <div className="card p-6 text-center text-gray-500">
          まだ Shopee 店舗を連携していません。先に「Shopee 店舗連携」画面で接続してください。
        </div>
      ) : (
        connections.map((c) => {
          const edit = edits[c.connection_id];
          if (!edit) return null;
          const overrideEntries = Object.entries(edit.overrides)
            .map(([pid, enabled]) => ({
              product_id: Number(pid),
              sync_enabled: enabled,
              info: c.overrides.find((o) => o.product_id === Number(pid)),
            }))
            .sort((a, b) => a.product_id - b.product_id);

          return (
            <section key={c.connection_id} className="card p-4 space-y-3">
              <h2 className="font-semibold">
                {c.shop_name || c.shop_id} ({c.country_code})
              </h2>

              <div className="text-sm space-x-4">
                <span>店舗デフォルト:</span>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    checked={!edit.default_enabled}
                    onChange={() => toggleDefault(c.connection_id, false)}
                  />
                  同期しない (OFF)
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    checked={edit.default_enabled}
                    onChange={() => toggleDefault(c.connection_id, true)}
                  />
                  同期する (ON)
                </label>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">
                  個別設定 (デフォルトと違う商品)
                </div>
                {overrideEntries.length === 0 ? (
                  <p className="text-xs text-gray-500">個別設定なし</p>
                ) : (
                  <ul className="divide-y text-sm">
                    {overrideEntries.map((o) => (
                      <li
                        key={o.product_id}
                        className="py-1 flex justify-between items-center"
                      >
                        <div>
                          <div className="font-mono text-xs">
                            JAN {o.info?.jan || `product_id=${o.product_id}`}
                          </div>
                          <div className="text-xs text-gray-500">
                            {o.info?.product_name || ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={o.sync_enabled}
                              onChange={(e) =>
                                setOverride(
                                  c.connection_id,
                                  o.product_id,
                                  e.target.checked,
                                )
                              }
                            />
                            同期 ON
                          </label>
                          <button
                            onClick={() =>
                              removeOverride(c.connection_id, o.product_id)
                            }
                            className="text-xs text-red-600 underline"
                          >
                            個別設定を削除
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {/* 個別追加 */}
                <div className="border-t pt-2 mt-2">
                  <div className="text-xs text-gray-500 mb-1">
                    + 個別設定を追加 (商品名 or JAN を 2 文字以上で検索)
                  </div>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="商品名 or JAN"
                      value={productSearch[c.connection_id] || ""}
                      onChange={(e) =>
                        setProductSearch((s) => ({
                          ...s,
                          [c.connection_id]: e.target.value,
                        }))
                      }
                      className="input text-sm"
                    />
                    <button
                      onClick={() => searchProducts(c.connection_id)}
                      className="btn-secondary text-xs"
                    >
                      検索
                    </button>
                  </div>
                  {(productResults[c.connection_id] || []).length > 0 && (
                    <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto border rounded p-2">
                      {(productResults[c.connection_id] || []).map((p) => {
                        const alreadyAdded =
                          edit.overrides[p.id] !== undefined ||
                          edit.removed.has(p.id);
                        return (
                          <li
                            key={p.id}
                            className="flex justify-between items-center"
                          >
                            <span>
                              <span className="font-mono">{p.jan}</span> —{" "}
                              {p.product_name}
                            </span>
                            <button
                              disabled={alreadyAdded}
                              onClick={() =>
                                setOverride(c.connection_id, p.id, true)
                              }
                              className="btn-secondary text-xs"
                            >
                              {alreadyAdded ? "追加済" : "ON で追加"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => handleSave(c.connection_id)}
                  disabled={!edit.dirty || savingConnId === c.connection_id}
                  className="btn-primary text-sm"
                >
                  {savingConnId === c.connection_id ? "保存中…" : "保存"}
                </button>
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
